/**
 * ZIP file layout (simplified):
 *
 *   [ Local File Headers + file data   ]  ← each file's compressed bytes
 *   [ Central Directory (CD)           ]  ← index of every file (name, size, offset)
 *   [ End of Central Directory (EOCD)  ]  ← ~22-byte footer pointing at the CD
 *
 * To list files in a ZIP, we read the EOCD at the end, use it to find the CD,
 * then walk the CD entries. We never touch the actual file payloads — all we
 * need is the filename metadata in the CD.
 */

interface ZipFileEntry {
  name: string;
  isDirectory: boolean;
}

// 4-byte markers identifying EOCD records and CD entries. They look reversed
// vs the ZIP spec's "PK\x05\x06" / "PK\x01\x02" because getUint32 assembles
// the bytes back-to-front.
const EOCD_SIGNATURE = 0x06054b50;
const CDFH_SIGNATURE = 0x02014b50;

// EOCD is 22 bytes minimum, with an optional trailing comment up to 65535 bytes.
// So the EOCD record is guaranteed to start within the last (22 + 65535) bytes
// of the file — that's the window we need to scan to find it.
const EOCD_MIN_SIZE = 22;
const MAX_EOCD_COMMENT_SIZE = 0xffff;

const readBlobAsUint8 = (blob: Blob): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const buf = e.target?.result as ArrayBuffer;
      resolve(new Uint8Array(buf));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(blob);
  });
};

const parseZipEntries = async (file: File): Promise<ZipFileEntry[]> => {
  if (file.size < EOCD_MIN_SIZE) {
    throw new Error('File is too small to be a valid ZIP');
  }

  const tailSize = Math.min(file.size, EOCD_MIN_SIZE + MAX_EOCD_COMMENT_SIZE);
  const tail = await readBlobAsUint8(file.slice(file.size - tailSize));
  const tailView = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);

  let eocdOffset = -1;
  for (let i = tail.length - EOCD_MIN_SIZE; i >= 0; i--) {
    if (tailView.getUint32(i, true) === EOCD_SIGNATURE) {
      eocdOffset = i;
      break;
    }
  }

  if (eocdOffset === -1) {
    throw new Error('Not a valid ZIP file.');
  }

  const totalEntries = tailView.getUint16(eocdOffset + 10, true);
  const cdSize = tailView.getUint32(eocdOffset + 12, true);
  const cdOffset = tailView.getUint32(eocdOffset + 16, true);

  if (
    totalEntries === 0xffff ||
    cdSize === 0xffffffff ||
    cdOffset === 0xffffffff
  ) {
    throw new Error('ZIP64 format is not supported');
  }

  if (totalEntries === 0) {
    return [];
  }

  const cd = await readBlobAsUint8(file.slice(cdOffset, cdOffset + cdSize));
  const cdView = new DataView(cd.buffer, cd.byteOffset, cd.byteLength);

  const entries: ZipFileEntry[] = [];
  let offset = 0;

  for (let i = 0; i < totalEntries; i++) {
    if (offset + 46 > cd.length) {
      throw new Error('Central Directory is truncated');
    }
    if (cdView.getUint32(offset, true) !== CDFH_SIGNATURE) {
      throw new Error('Invalid Central Directory entry signature');
    }

    const filenameLength = cdView.getUint16(offset + 28, true);
    const extraFieldLength = cdView.getUint16(offset + 30, true);
    const commentLength = cdView.getUint16(offset + 32, true);

    const filenameBytes = cd.subarray(
      offset + 46,
      offset + 46 + filenameLength,
    );
    const filename = new TextDecoder('utf-8', { fatal: false }).decode(
      filenameBytes,
    );

    entries.push({
      name: filename,
      isDirectory: filename.endsWith('/'),
    });

    offset += 46 + filenameLength + extraFieldLength + commentLength;
  }

  return entries;
};

export const validateZipContainsOnlyPdfs = async (
  file: File,
): Promise<{
  isValid: boolean;
  error?: string;
}> => {
  try {
    if (
      file.type !== 'application/zip' &&
      !file.name.toLowerCase().endsWith('.zip')
    ) {
      return {
        isValid: false,
        error: 'File must be a ZIP file',
      };
    }

    const entries = await parseZipEntries(file);

    if (entries.length === 0) {
      return {
        isValid: false,
        error: 'ZIP file appears to be empty',
      };
    }

    for (const entry of entries) {
      if (entry.isDirectory) {
        continue;
      }

      const fileName = entry.name.toLowerCase();
      if (!fileName.endsWith('.pdf')) {
        return {
          isValid: false,
          error: `ZIP file contains non-PDF file: ${entry.name}`,
        };
      }
    }

    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      error: `Failed to validate ZIP file: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
};

export const validateFileSize = (
  files: File | File[],
  maxSizeInMB: number = 350,
  validateTotal: boolean = false,
): {
  isValid: boolean;
  error?: string;
} => {
  const maxSize = maxSizeInMB * 1024 * 1024;
  const fileArray = Array.isArray(files) ? files : [files];

  if (validateTotal) {
    const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxSize) {
      return {
        isValid: false,
        error: `Total file size must not exceed ${maxSizeInMB} MB`,
      };
    }
  } else {
    for (const file of fileArray) {
      if (file.size > maxSize) {
        return {
          isValid: false,
          error: `File size must not exceed ${maxSizeInMB} MB`,
        };
      }
    }
  }

  return { isValid: true };
};

export const zipContainsOnlyPdfsRule = {
  validate: async (files: File[]) => {
    for (const file of files) {
      const validation = await validateZipContainsOnlyPdfs(file);
      if (!validation.isValid) {
        return validation.error || 'ZIP file must contain only PDF documents.';
      }
    }
    return null;
  },
  message: 'ZIP file must contain only PDF documents.',
};

export const fileSizeRule = (
  maxSizeInMB: number,
  validateTotal: boolean = false,
) => ({
  validate: (files: File[]) => {
    const validation = validateFileSize(files, maxSizeInMB, validateTotal);
    if (!validation.isValid) {
      return (
        validation.error ||
        (validateTotal
          ? `Total file size must not exceed ${maxSizeInMB} MB.`
          : `File size must not exceed ${maxSizeInMB} MB.`)
      );
    }
    return null;
  },
  message: validateTotal
    ? `Total file size must not exceed ${maxSizeInMB} MB.`
    : `File size must not exceed ${maxSizeInMB} MB.`,
});
